"use client";

import { Activity, HeartHandshake, Leaf } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pillars = [{
  icon: HeartHandshake,
  title: "Bespoke Strategy",
  description: "Every consultation is tailored to your unique goals, with dedicated experts who prioritize your vision and provide personalized strategic guidance."
}, {
  icon: Activity,
  title: "Precision Analytics",
  description: "Utilizing advanced analytical tools, we deliver precise, actionable intelligence so your business decisions are built on evidence, not intuition."
}, {
  icon: Leaf,
  title: "Scalable Growth",
  description: "From initial strategy to long-term execution, we build scalable frameworks that ensure your business remains resilient, efficient, and ready for the future."
}];
export default function Benefits() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const card1 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const card2 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const card3 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const curve = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const cardRefs = [card1, card2, card3];
  return <section data-section-id="14560" id="core-benefits" className="relative bg-background text-foreground border-t border-border py-24 md:py-32">
      <div className="mx-auto max-w-[1280px] px-6 md:px-10 lg:px-16">
        <div ref={header.ref} className="mx-auto max-w-[680px] text-center transition-all duration-700 ease-out" style={{
        opacity: header.isVisible ? 1 : 0,
        transform: header.isVisible ? "translateY(0)" : "translateY(16px)",
        transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)"
      }}>
          <h2 className="font-default font-bold text-foreground text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.1]">
            The Strategic{" "}
            <span className="relative inline-block italic font-normal">
              advantage
              <span ref={curve.ref} aria-hidden="true" className="pointer-events-none absolute left-1/2 -translate-x-1/2" style={{
              bottom: "-0.35em",
              width: "110%"
            }}>
                <svg viewBox="0 0 120 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" preserveAspectRatio="none">
                  <path d="M2 8 Q 30 2, 60 6 T 118 4" stroke="var(--chart-2)" strokeWidth="3" strokeLinecap="round" fill="none" style={{
                  strokeDasharray: 200,
                  strokeDashoffset: curve.isVisible ? 0 : 200,
                  transition: "stroke-dashoffset 900ms ease-out 200ms"
                }} />
                </svg>
              </span>
            </span>
            .
          </h2>
          <p className="mt-6 font-default font-normal text-muted-foreground text-lg md:text-xl tracking-normal leading-relaxed">
            Three core pillars define our partnership — dedicated collaboration,
            rigorous analysis, and scalable solutions designed to drive your
            success for years to come.
          </p>
        </div>

        <div className="mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          const reveal = cardRefs[i];
          return <div data-index={i} key={i} ref={reveal.ref} style={{
            opacity: reveal.isVisible ? 1 : 0,
            transform: reveal.isVisible ? "translateY(0)" : "translateY(16px)",
            transition: `opacity 700ms cubic-bezier(0.22, 0.61, 0.36, 1) ${i * 80}ms, transform 700ms cubic-bezier(0.22, 0.61, 0.36, 1) ${i * 80}ms`
          }}>
                <Card className="group h-full bg-card border border-border rounded-lg p-10 transition-all duration-300 ease-out hover:border-[color:var(--chart-2)]/40 shadow-md">
                  <CardContent className="px-0 flex flex-col h-full">
                    <div className="text-[color:var(--chart-2)]">
                      <Icon className="size-12" strokeWidth={1.5} aria-hidden="true" />
                    </div>
                    <h3 className="mt-8 font-default font-semibold text-foreground text-2xl md:text-3xl tracking-tight leading-tight">
                      {pillar.title}
                    </h3>
                    <p className="mt-4 font-default font-normal text-foreground/85 leading-relaxed">
                      {pillar.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}